import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Alert,
    AlertTitle,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import { RootState } from '../../configureStore';

interface ConfirmDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
    unreviewedForms: string[];
    viewedPages: Record<string, Set<number>>;
    formPageCounts: Record<string, number>;
}

const ConfirmDialog = ({
    open,
    onClose,
    onConfirm,
    unreviewedForms,
    viewedPages,
    formPageCounts,
}: ConfirmDialogProps) => {
    const hasChanges = useSelector<RootState, boolean>(({ returns }) => returns.hasChanges);
    const isSaving = useSelector<RootState, boolean>(({ status: { loading: l } }) => l['returns/SAVE_EXTRACTION']);
    const [acknowledgeUnreviewed, setAcknowledgeUnreviewed] = useState(false);

    const handleClose = () => {
        onClose();
        setAcknowledgeUnreviewed(false);
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Confirm Extraction Complete</DialogTitle>
            <DialogContent>
                {unreviewedForms.length > 0 ? (
                    <Alert
                        severity="warning"
                        sx={{ mb: 2 }}
                    >
                        <AlertTitle>Unreviewed Forms</AlertTitle>
                        <Typography
                            variant="body2"
                            sx={{ mb: 1 }}
                        >
                            The following forms have not been fully reviewed:
                        </Typography>
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                            {unreviewedForms.map((f) => (
                                <li key={f}>
                                    {f.toUpperCase()} ({viewedPages[f]?.size || 0}/{formPageCounts[f]} pages)
                                </li>
                            ))}
                        </ul>
                        <FormControlLabel
                            sx={{ mt: 2 }}
                            control={(
                                <Checkbox
                                    checked={acknowledgeUnreviewed}
                                    onChange={(e) => setAcknowledgeUnreviewed(e.target.checked)}
                                />
                              )}
                            label="I acknowledge these forms don't need review"
                        />
                    </Alert>
                ) : (
                    <Typography
                        variant="body1"
                        color="success.main"
                        sx={{ mb: 2 }}
                    >
                        All forms have been reviewed.
                    </Typography>
                )}
                {hasChanges && (
                    <Typography
                        variant="body2"
                        color="warning.main"
                    >
                        Note: Your changes will be saved when you confirm.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button
                    onClick={handleClose}
                    disabled={isSaving}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color="success"
                    disabled={isSaving || (unreviewedForms.length > 0 && !acknowledgeUnreviewed)}
                >
                    {isSaving ? 'Confirming...' : 'Confirm'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmDialog;
